import React, { useState } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { showToastr } from '../../../components/ToastrMessage/Toast.helper';
import './CSCOwnerCategories.styles.scss';

const STORAGE_KEY = 'cscCustomCategories';

const load = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    // Migrate old nested format { categoryName, subCategories: [{id, name}] } → flat rows
    const needsMigration = raw.some((item) => item.categoryName !== undefined);
    if (!needsMigration) return raw;
    const migrated = [];
    raw.forEach((item) => {
      if (item.category !== undefined) {
        migrated.push(item);
      } else {
        (item.subCategories || []).forEach((sub) => {
          migrated.push({
            id: sub.id || genId(),
            category: item.categoryName || '',
            subCategory: sub.name || '',
            createdAt: item.createdAt || new Date().toISOString(),
          });
        });
      }
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
    return migrated;
  } catch { return []; }
};

const genId = () => `id-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const CSCOwnerCategories = () => {
  const [entries, setEntries] = useState(load);

  const [showAdd, setShowAdd]       = useState(false);
  const [addCat, setAddCat]         = useState('');
  const [addSub, setAddSub]         = useState('');
  const [addCatError, setAddCatError] = useState('');

  const [editId, setEditId]         = useState(null);
  const [editCat, setEditCat]       = useState('');
  const [editSub, setEditSub]       = useState('');
  const [editCatError, setEditCatError] = useState('');

  const [deleteId, setDeleteId]     = useState(null);
  const [search, setSearch]         = useState('');

  const filteredEntries = search.trim()
    ? entries.filter((e) => {
        const q = search.toLowerCase();
        return (
          e.category?.toLowerCase().includes(q) ||
          e.subCategory?.toLowerCase().includes(q)
        );
      })
    : entries;

  const persist = (data) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setEntries(data);
  };

  const isDupCat = (cat, excludeId = null) =>
    entries.some(
      (e) => e.id !== excludeId && e.category.trim().toLowerCase() === cat.trim().toLowerCase()
    );

  const openAdd = () => {
    setShowAdd(true);
    setAddCat('');
    setAddSub('');
    setAddCatError('');
    setDeleteId(null);
    setEditId(null);
  };

  const handleAdd = () => {
    const cat = addCat.trim();
    const sub = addSub.trim();
    if (!cat || !sub) return;
    if (isDupCat(cat)) {
      setAddCatError('This category already exists');
      return;
    }
    persist([...entries, {
      id: genId(),
      category: cat,
      subCategory: sub,
      createdAt: new Date().toISOString(),
    }]);
    setAddCat('');
    setAddSub('');
    setAddCatError('');
    setShowAdd(false);
    showToastr('success', 'Entry added successfully.');
  };

  const handleSaveEdit = (id) => {
    const cat = (editCat || '').trim();
    const sub = (editSub || '').trim();
    if (!cat || !sub) return;
    if (isDupCat(cat, id)) {
      setEditCatError('This category already exists');
      return;
    }
    persist(entries.map((e) =>
      e.id === id ? { ...e, category: cat, subCategory: sub } : e
    ));
    setEditId(null);
    setEditCatError('');
    showToastr('success', 'Entry updated.');
  };

  const handleDelete = (id) => {
    persist(entries.filter((e) => e.id !== id));
    setDeleteId(null);
    showToastr('success', 'Entry deleted.');
  };

  const existingCats = [...new Set(entries.map((e) => e.category))];

  return (
    <div className="cocat">

      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="cocat__header">
        <div className="cocat__header-text">
          <h2 className="cocat__title">My Service Categories</h2>
          <p className="cocat__subtitle">
            Each row is one category and sub-category pair — available across all states when registering services
          </p>
        </div>
        <Button
          variant="contained" disableElevation
          startIcon={<AddCircleOutlineIcon />}
          className="cocat__add-btn"
          onClick={openAdd}
        >
          Add
        </Button>
      </div>

      {/* ── Search toolbar ──────────────────────────────────────── */}
      {entries.length > 0 && (
        <div className="cocat__toolbar">
          <TextField
            size="small" variant="outlined"
            placeholder="Search by category or sub-category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="cocat__search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <span className="cocat__count">
            {filteredEntries.length} of {entries.length} entr{entries.length !== 1 ? 'ies' : 'y'}
          </span>
        </div>
      )}

      {/* ── Add form (full-width) ──────────────────────────────── */}
      {showAdd && (
        <div className="cocat__add-form">
          <div className="cocat__add-form__field">
            <TextField
              size="small" variant="outlined" fullWidth
              placeholder="Category name"
              value={addCat}
              onChange={(e) => { setAddCat(e.target.value); setAddCatError(''); }}
              inputProps={{ list: 'cocat-existing-cats' }}
              error={!!addCatError}
              helperText={addCatError}
              autoFocus
            />
            <datalist id="cocat-existing-cats">
              {existingCats.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div className="cocat__add-form__field">
            <TextField
              size="small" variant="outlined" fullWidth
              placeholder="Sub-category name"
              value={addSub}
              onChange={(e) => setAddSub(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
                if (e.key === 'Escape') setShowAdd(false);
              }}
            />
          </div>
          <div className="cocat__add-form__actions">
            <IconButton size="small" className="cocat__icon-confirm" title="Save" onClick={handleAdd}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" className="cocat__icon-cancel" title="Cancel" onClick={() => setShowAdd(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}

      {/* ── No search results ──────────────────────────────────── */}
      {search.trim() && filteredEntries.length === 0 && (
        <div className="cocat__no-results">
          No entries match <strong>"{search}"</strong>
        </div>
      )}

      {/* ── 2-column card grid ─────────────────────────────────── */}
      {entries.length > 0 && (
        <div className="cocat__grid">
          {filteredEntries.map((entry) => (
            <div key={entry.id} className={`cocat__card${deleteId === entry.id ? ' cocat__card--deleting' : ''}`}>

              {editId === entry.id ? (
                /* Edit mode */
                <div className="cocat__card-edit">
                  <TextField
                    size="small" variant="outlined" fullWidth
                    label="Category"
                    value={editCat}
                    onChange={(e) => { setEditCat(e.target.value); setEditCatError(''); }}
                    error={!!editCatError}
                    helperText={editCatError}
                    autoFocus
                  />
                  <TextField
                    size="small" variant="outlined" fullWidth
                    label="Sub-category"
                    value={editSub}
                    onChange={(e) => setEditSub(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit(entry.id);
                      if (e.key === 'Escape') setEditId(null);
                    }}
                  />
                  <div className="cocat__card-edit-actions">
                    <IconButton size="small" className="cocat__icon-confirm" onClick={() => handleSaveEdit(entry.id)}>
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" className="cocat__icon-cancel" onClick={() => { setEditId(null); setEditCatError(''); }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              ) : deleteId === entry.id ? (
                /* Delete confirmation */
                <div className="cocat__del-confirm">
                  <p className="cocat__del-text">
                    Delete <strong>{entry.category}</strong> → <strong>{entry.subCategory}</strong>?
                  </p>
                  <div className="cocat__del-actions">
                    <Button size="small" variant="contained" color="error" disableElevation onClick={() => handleDelete(entry.id)}>
                      Yes, Delete
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setDeleteId(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                /* Normal card view */
                <>
                  <div className="cocat__card-header">
                    <span className="cocat__card-cat">{entry.category}</span>
                    <div className="cocat__card-actions">
                      <IconButton
                        size="small" className="cocat__icon-edit" title="Edit"
                        onClick={() => { setEditId(entry.id); setEditCat(entry.category || ''); setEditSub(entry.subCategory || ''); setEditCatError(''); setDeleteId(null); }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small" className="cocat__icon-del" title="Delete"
                        onClick={() => { setDeleteId(entry.id); setEditId(null); }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  <span className="cocat__card-sub">{entry.subCategory}</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────────── */}
      {entries.length === 0 && !showAdd && (
        <div className="cocat__empty">
          <CategoryOutlinedIcon className="cocat__empty-icon" />
          <p className="cocat__empty-title">No entries yet</p>
          <p className="cocat__empty-sub">
            Add category and sub-category pairs to use while registering services.
          </p>
          <Button variant="contained" disableElevation className="cocat__add-btn" onClick={openAdd}>
            Add First Entry
          </Button>
        </div>
      )}
    </div>
  );
};

export default CSCOwnerCategories;
